import { TrendingUp, PieChart, Shield, Landmark, CreditCard, BarChart3, ArrowRight, Globe, CheckCircle, Plus, Minus } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useState } from 'react';
import BookingModal from './modals/BookingModal';
import CalculatorModal from './modals/CalculatorModal';
import SEO from './SEO';
import { pageSEO, faqSchemas } from '../seoData';

const Services = () => {
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [isCalculatorModalOpen, setIsCalculatorModalOpen] = useState(false);
  const [expandedFaq, setExpandedFaq] = useState(null);

  const services = [
    {
      id: 'equity',
      title: 'Equity Trading',
      icon: TrendingUp,
      shortDesc: 'SEBI registered sub-broker offering comprehensive trading facilities in Equity, F&O, and Commodities.',
      fullDesc: 'We are SEBI registered sub-broker with Sunidhi, a national level stockbroker and member of NSE/BSE/MCX. We offer clients trading facilities in these exchanges for Equity, Future & Options, and Commodities segments.',
      features: [
        'SEBI registered sub-broker with Sunidhi (NSE/BSE/MCX member)',
        'Trading facilities in Equity, Future & Options, and Commodities',
        'Research reports and market outlook',
        'Technical levels and guidance in option trading',
        'Stock levels for trading',
        'SIP in direct stocks',
        'DP (Depository Participant) facilities',
        'Online back office access',
        'Trading platform on desktop and mobile application',
        'Dedicated dealer and back-office support',
        'Audit of existing stock portfolio for registered clients'
      ],
      minInvestment: '₹1 lakh',
      expectedReturns: '12-15% annually',
      riskLevel: 'High',
      timeHorizon: '3+ years'
    },
    {
      id: 'mutual_funds',
      title: 'Mutual Funds',
      icon: PieChart,
      shortDesc: 'Comprehensive mutual fund advisory with funds from all major AMCs, research-backed recommendations, and portfolio tracking.',
      fullDesc: 'We offer Mutual Funds of all the major AMCs in the country. Our goal is to provide a choice of well-blended portfolio of Mutual Funds to our clients. We do detailed research and track the performance of the MFs on a quarterly basis. We look at the relevant parameters of the fund to align with the wealth creation goal of the client and then suggest the investment plan.',
      features: [
        'Mutual Funds from all major AMCs in the country',
        'Well-blended portfolio selection for clients',
        'Detailed research and quarterly performance tracking',
        'Fund parameters aligned with client\'s wealth creation goals',
        'Customized investment plan suggestions',
        'Pro SIP or STP approach, avoid lump sum directly in equity',
        'Choice of funds in each category based on risk appetite',
        'Liquid funds for parking unutilized funds',
        'Auditing of current Mutual Funds for registered clients',
        'Online addition or redemption of funds',
        'Mobile app for daily portfolio tracking'
      ],
      minInvestment: '₹500 (SIP)',
      expectedReturns: '10-12% annually',
      riskLevel: 'Moderate to High',
      timeHorizon: '3+ years'
    },
    {
      id: 'pms',
      title: 'Portfolio Management Services (PMS)',
      icon: Shield,
      shortDesc: 'PMS from top fund managers in the country with proven 5-year track record. Minimum investment ₹50 lakhs.',
      fullDesc: 'We offer PMS of the top fund managers in the country. The size and performance of the PMS for the last 5 years is one of the key parameters we look at. We advise only those clients who have the risk appetite to invest in a PMS. PMS – Portfolio Management Service is where professional fund managers have the mandate to invest (buy and sell assets) on the client\'s behalf.',
      features: [
        'PMS from top fund managers in the country',
        'Performance track record of last 5 years analyzed',
        'Fund size evaluation as key parameter',
        'Client suitability based on risk appetite',
        'Professional mandate to buy and sell assets',
        'Customized investment strategies',
        'Direct equity ownership',
        'Regular performance reporting',
        'Dedicated relationship manager'
      ],
      minInvestment: '₹50 lakhs',
      expectedReturns: '12-18% annually',
      riskLevel: 'Moderate to High',
      timeHorizon: '3+ years'
    },
    {
      id: 'multi_asset_aif',
      title: 'AIF (Alternative Investment Fund)',
      icon: PieChart,
      shortDesc: 'Multi Asset and Debt AIF solutions for high-net-worth investors seeking diversification and superior returns.',
      fullDesc: 'We advise clients on Multi Asset AIF and Debt AIF – Alternative Investment Funds. Multi Asset AIF provides exposure to various asset classes including equity, debt, commodities, REITs, and alternative investments. We also advise clients on Debt AIF which gives more than 12% per annum returns. This helps clients to diversify from equity and also earn a monthly income. We advise our clients in selecting different performing AIF providers based on their investment philosophy and track record.',
      features: [
        'SEBI regulated Alternative Investment Fund',
        'Multi-asset class diversification (Multi Asset AIF)',
        'Debt AIF offering 12%+ per annum returns',
        'Monthly income generation through Debt AIF',
        'Helps diversify from equity investments',
        'Professional asset allocation strategies',
        'Access to alternative investments',
        'Dynamic rebalancing capabilities',
        'Institutional-grade investment opportunities'
      ],
      minInvestment: '₹1 crore',
      expectedReturns: '10-15% annually',
      riskLevel: 'Moderate to High',
      timeHorizon: '3-7 years'
    },
    {
      id: 'bonds',
      title: 'Corporate & Government Bonds',
      icon: Landmark,
      shortDesc: 'Fixed-income investments in GOI Bonds, State Subsidiary Bonds, and Corporate Bonds with minimum A+ ratings for client safety.',
      fullDesc: 'We offer BONDS in 3 categories: GOI Bonds, State Subsidiary bonds, and Corporate Bonds. Safety of client money is our primary focus, and we do not advise clients to buy any bonds below A+ ratings. We help clients to buy Bonds on the exchange or source them offline.',
      features: [
        'GOI Bonds (Government of India Bonds)',
        'State Subsidiary bonds',
        'Corporate Bonds',
        'Only bonds with A+ rating or above recommended',
        'Safety of client money is primary focus',
        'Bond purchases on exchange',
        'Offline bond sourcing available',
        'Credit rating analysis',
        'Yield optimization strategies',
        'Maturity matching with goals',
        'Regular interest income'
      ],
      minInvestment: '₹5 lakhs',
      expectedReturns: '7-9% annually',
      riskLevel: 'Low to Moderate',
      timeHorizon: '1-10 years'
    },
    {
      id: 'fixed_deposits',
      title: 'NBFC Fixed Deposits',
      icon: CreditCard,
      shortDesc: 'Higher-yielding fixed deposits from reputed NBFCs for conservative investors.',
      fullDesc: 'Fixed Deposits are popular instruments for parking funds that might be required on a rainy day. We help invest in FDs of well-rated NBFCs that offer better returns than traditional bank FDs while maintaining safety.',
      features: [
        'Higher returns than bank FDs',
        'Flexible tenure options',
        'CRISIL/ICRA rated NBFCs',
        'Quarterly interest options',
        'Easy online investment process'
      ],
      minInvestment: '₹25,000',
      expectedReturns: '8-10% annually',
      riskLevel: 'Low',
      timeHorizon: '1-5 years'
    },
    {
      id: 'commodities',
      title: 'Commodity Trading',
      icon: BarChart3,
      shortDesc: 'Exchange-traded commodity investments through Sunidhi (MCX member) with mobile app and full back-office support.',
      fullDesc: 'We offer Commodity trading through Sunidhi, a member of MCX (Multi Commodity Exchange). Commodity trading provides an excellent opportunity for portfolio diversification. We offer trading in various commodities including precious metals, energy, and agricultural products through recognized exchanges.',
      features: [
        'Trading through Sunidhi (MCX member)',
        'Mobile app for commodity trading',
        'Full back-office support for client reports',
        'Net positions reporting',
        'Diversified commodity exposure',
        'Hedge against inflation',
        'Professional market insights',
        'Risk management tools',
        'Real-time market updates'
      ],
      minMargin: '₹50,000',
      expectedReturns: 'Variable (market dependent)',
      riskLevel: 'High',
      timeHorizon: 'Short to medium term'
    },
    {
      id: 'us_markets',
      title: 'US Market Investments',
      icon: Globe,
      shortDesc: 'Access global opportunities through US stock markets via trusted brokers like Invested for portfolio diversification.',
      fullDesc: 'We facilitate investments in US stock markets through reputable brokers like Invested, allowing Indian investors to access global giants like Apple, Google, Amazon, and Microsoft. US market exposure provides geographical diversification and access to sectors not well-represented in Indian markets.',
      features: [
        'Access to US stocks, ETFs, and mutual funds',
        'Investment through trusted brokers like Invested',
        'Exposure to global tech giants and Fortune 500 companies',
        'Portfolio diversification beyond Indian markets',
        'Access to sectors underrepresented in India (tech, pharma, aerospace)',
        'Currency diversification (USD exposure)',
        'LRS (Liberalized Remittance Scheme) compliant investments',
        'Professional guidance on US market investments'
      ],
      pros: [
        'Geographic and currency diversification',
        'Access to world-leading companies and innovation hubs',
        'Exposure to USD - hedge against rupee depreciation',
        'Larger market depth and liquidity',
        'Access to advanced sectors and cutting-edge technology companies',
        'Potential for higher growth in developed markets'
      ],
      cons: [
        'Currency exchange risk (USD/INR fluctuations)',
        'Tax implications - US dividend withholding tax (up to 25%)',
        'Complex tax filing requirements (IRS, FATCA compliance)',
        'LRS limit of $250,000 per financial year',
        'Higher brokerage and transaction costs',
        'Time zone differences affecting trading hours',
        'Need for understanding of US market regulations'
      ],
      minInvestment: 'As per LRS norms',
      expectedReturns: '8-12% annually (market dependent)',
      riskLevel: 'Moderate to High',
      timeHorizon: '3-5 years'
    }
  ];
  
  const getRiskColor = (risk) => {
    switch (risk) {
      case 'Low': return 'text-green-600 bg-green-100';
      case 'Low to Moderate': return 'text-yellow-600 bg-yellow-100';
      case 'Moderate': return 'text-orange-600 bg-orange-100';
      case 'Moderate to High': return 'text-red-500 bg-red-100';
      case 'High': return 'text-red-600 bg-red-100';
      case 'Very High': return 'text-red-700 bg-red-200';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO {...pageSEO.services} faqJsonLd={faqSchemas.services} />
      {/* Hero Section - Light & Clean */}
      <section className="relative bg-gradient-to-br from-blue-50 via-white to-purple-50 pt-20 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="mb-6">
            <span className="inline-block bg-blue-600 text-white px-4 py-2 rounded-full text-sm font-semibold tracking-wide">
              COMPREHENSIVE INVESTMENT SOLUTIONS
            </span>
          </div>
          <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight" data-testid="services-title">
            Financial Services for
            <span className="block bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              Every Investment Goal
            </span>
          </h1>
          <p className="text-xl text-gray-600 max-w-4xl mx-auto mb-8 leading-relaxed" data-testid="services-description">
            As a trusted investment advisor India and SEBI-registered NRI investment consultant, we offer comprehensive 
            wealth management India solutions. From equity trading to mutual funds, our portfolio management India services 
            help you achieve your financial dreams.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => setIsBookingModalOpen(true)}
              className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-4 rounded-xl font-bold text-lg hover:from-blue-700 hover:to-purple-700 transition-all transform hover:scale-105 inline-flex items-center justify-center shadow-xl"
            >
              Book Free Consultation
              <ArrowRight className="ml-2 h-5 w-5" />
            </button>
            <button
              onClick={() => setIsCalculatorModalOpen(true)}
              className="border-2 border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white px-8 py-4 rounded-xl font-bold text-lg transition-all inline-flex items-center justify-center"
            >
              Calculate Returns
            </button>
          </div>
        </div>
      </section>
      
      {/* Services Grid */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <div key={service.id} className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow p-8" data-testid={`service-${index}`}>
                  {/* Service Header */}
                  <div className="flex items-start space-x-4 mb-6">
                    <div className="bg-blue-100 p-3 rounded-lg flex-shrink-0">
                      <Icon className="h-6 w-6 text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-2xl font-bold text-gray-900 mb-2">{service.title}</h3>
                      <p className="text-gray-600 text-sm">{service.shortDesc}</p>
                    </div>
                  </div>
                  
                  {/* Service Details */}
                  <div className="space-y-4">
                    <p className="text-gray-700 leading-relaxed">{service.fullDesc}</p>
                    
                    {/* Key Features */}
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">Key Features:</h4>
                      <ul className="text-sm text-gray-600 space-y-1">
                        {service.features.map((feature, featureIndex) => (
                          <li key={featureIndex} className="flex items-start">
                            <span className="text-blue-600 mr-2">•</span>
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>
                    
                    {/* Pros and Cons (if available) */}
                    {service.pros && service.cons && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4">
                        <div>
                          <h4 className="font-semibold text-green-700 mb-2">Advantages:</h4>
                          <ul className="text-sm text-gray-600 space-y-1">
                            {service.pros.map((pro, proIndex) => (
                              <li key={proIndex} className="flex items-start">
                                <span className="text-green-600 mr-2">✓</span>
                                {pro}
                              </li>
                            ))}
                          </ul>
                        </div>
                        <div>
                          <h4 className="font-semibold text-red-700 mb-2">Considerations:</h4>
                          <ul className="text-sm text-gray-600 space-y-1">
                            {service.cons.map((con, conIndex) => (
                              <li key={conIndex} className="flex items-start">
                                <span className="text-red-600 mr-2">⚠</span>
                                {con}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    )}
                    {/* Investment Details */}
                    <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-100">
                      <div>
                        <div className="text-xs text-gray-500 uppercase tracking-wide">
                          {service.id === 'commodities' ? 'Min Margin' : 'Min Investment'}
                        </div>
                        <div className="font-semibold text-gray-900">
                          {service.minMargin || service.minInvestment}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500 uppercase tracking-wide">Expected Returns</div>
                        <div className="font-semibold text-green-600">{service.expectedReturns}</div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500 uppercase tracking-wide">Risk Level</div>
                        <div className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${getRiskColor(service.riskLevel)}`}>
                          {service.riskLevel}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500 uppercase tracking-wide">Time Horizon</div>
                        <div className="font-semibold text-gray-900">{service.timeHorizon}</div>
                      </div>
                    </div>
                    
                    {/* CTA Button */}
                    <div className="pt-6 border-t border-gray-100 mt-6">
                      <Link
                        to="/contact"
                        className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-xl font-bold text-center hover:from-blue-700 hover:to-purple-700 transition-all transform hover:scale-105 inline-flex items-center justify-center shadow-lg"
                      >
                        Get Started with {service.title}
                        <ArrowRight className="ml-2 h-5 w-5" />
                      </Link>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>
      
      {/* Portfolio Audit Callout */}
      <section className="py-16 bg-gradient-to-br from-green-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
              {/* Left Content */}
              <div className="p-8 lg:p-12 flex flex-col justify-center">
                <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
                  Get Your Portfolio Audited for FREE
                </h2>
                <p className="text-lg text-gray-600 mb-6">
                  Not sure if your current investments are performing well? We offer a complimentary audit of your existing stock or mutual fund portfolio to help you identify opportunities and optimize your returns.
                </p>
                
                <div className="space-y-4 mb-8">
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <div className="flex items-center justify-center h-8 w-8 rounded-full bg-green-100 text-green-600">
                        <span className="text-lg">✓</span>
                      </div>
                    </div>
                    <p className="ml-4 text-gray-700">
                      <strong>Comprehensive Analysis:</strong> Detailed review of your current holdings, asset allocation, and performance
                    </p>
                  </div>
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <div className="flex items-center justify-center h-8 w-8 rounded-full bg-green-100 text-green-600">
                        <span className="text-lg">✓</span>
                      </div>
                    </div>
                    <p className="ml-4 text-gray-700">
                      <strong>Expert Recommendations:</strong> Actionable insights to improve portfolio returns and reduce risks
                    </p>
                  </div>
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <div className="flex items-center justify-center h-8 w-8 rounded-full bg-green-100 text-green-600">
                        <span className="text-lg">✓</span>
                      </div>
                    </div>
                    <p className="ml-4 text-gray-700">
                      <strong>No Obligation:</strong> Free audit with personalized suggestions – no strings attached
                    </p>
                  </div>
                </div>
                
                <div className="flex flex-col sm:flex-row gap-4">
                  <Link
                    to="/contact"
                    className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-lg text-white bg-green-600 hover:bg-green-700 transition-colors"
                  >
                    Submit Your Portfolio
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                  <button
                    onClick={() => setIsBookingModalOpen(true)}
                    className="inline-flex items-center justify-center px-6 py-3 border-2 border-green-600 text-base font-medium rounded-lg text-green-600 bg-white hover:bg-green-50 transition-colors"
                  >
                    Book Free Consultation
                  </button>
                </div>
              </div>
              
              {/* Right Content - What to Submit */}
              <div className="bg-gradient-to-br from-green-600 to-blue-700 p-8 lg:p-12 text-white flex flex-col justify-center">
                <h3 className="text-2xl font-bold mb-6">What You Need to Submit:</h3>
                <div className="space-y-4">
                  <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
                    <h4 className="font-semibold text-lg mb-2">📊 For Stock Portfolio Audit:</h4>
                    <p className="text-blue-50">DP (Demat) Holding Statement from your broker showing your current equity holdings</p>
                  </div>
                  <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
                    <h4 className="font-semibold text-lg mb-2">📈 For Mutual Fund Audit:</h4>
                    <p className="text-blue-50">Consolidated Account Statement (CAS) from NSDL/CDSL or individual fund statements</p>
                  </div>
                  <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
                    <p className="text-sm text-blue-100">
                      <strong>Privacy Assured:</strong> Your financial information is handled with complete confidentiality and used only for portfolio analysis.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Investment Process */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4" data-testid="process-title">
              Our Investment Process
            </h2>
            <p className="text-lg text-gray-600">
              A systematic approach to wealth creation tailored to your unique needs
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="text-center" data-testid="process-step-1">
              <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-blue-600">1</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Consultation</h3>
              <p className="text-gray-600">Understanding your financial goals, risk tolerance, and investment timeline.</p>
            </div>
            
            <div className="text-center" data-testid="process-step-2">
              <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-blue-600">2</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Strategy</h3>
              <p className="text-gray-600">Developing a customized investment strategy based on your profile and objectives.</p>
            </div>
            
            <div className="text-center" data-testid="process-step-3">
              <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-blue-600">3</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Implementation</h3>
              <p className="text-gray-600">Executing the investment plan across selected instruments and asset classes.</p>
            </div>
            
            <div className="text-center" data-testid="process-step-4">
              <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-blue-600">4</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Monitoring</h3>
              <p className="text-gray-600">Regular portfolio reviews and rebalancing to ensure optimal performance.</p>
            </div>
          </div>
        </div>
      </section>
      
      {/* Call to Action */}
      <section className="py-20 bg-blue-900 text-white">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-6" data-testid="cta-title">
            Ready to Diversify Your Investment Portfolio?
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Let our experts help you choose the right mix of investments for your financial goals. 
            Start your wealth creation journey today.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => setIsBookingModalOpen(true)}
              data-testid="get-investment-advice-btn"
              className="bg-blue-500 hover:bg-blue-600 text-white px-8 py-4 rounded-lg font-semibold transition-colors inline-flex items-center justify-center"
            >
              Get Investment Advice
              <ArrowRight className="ml-2 h-5 w-5" />
            </button>
            <button
              onClick={() => setIsCalculatorModalOpen(true)}
              data-testid="calculate-potential-returns-btn"
              className="border-2 border-blue-300 text-blue-300 hover:bg-blue-300 hover:text-blue-900 px-8 py-4 rounded-lg font-semibold transition-colors inline-flex items-center justify-center"
            >
              Calculate Potential Returns
              <ArrowRight className="ml-2 h-5 w-5" />
            </button>
          </div>
        </div>
      </section>
      
      {/* FAQ Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-gray-600">
              Get answers to common questions about our services
            </p>
          </div>
          
          <div className="space-y-4">
            {[
              {
                question: "What are the minimum investment amounts?",
                answer: "Minimum investments vary: Equity Trading (₹1 lakh), Mutual Funds (₹5,000/month), PMS (₹50 lakhs), Bonds (₹5 lakhs), AIF (₹1 crore), Commodity (₹50,000 margin)."
              },
              {
                question: "Are you SEBI registered?",
                answer: "Yes, we are SEBI registered sub-brokers with Sunidhi Securities (NSE/BSE/MCX member). You can verify our credentials through SEBI's website."
              },
              {
                question: "Can NRIs invest through your services?",
                answer: "Absolutely! We specialize in NRI investments since 2009. We help with documentation, NRE/NRO accounts, and tax implications."
              },
              {
                question: "How do you charge for services?",
                answer: "Fees vary by service: Brokerage for equity, AMC commissions for mutual funds (at no extra cost), and percentage of AUM for PMS/AIF. Complete fee disclosure provided upfront."
              },
              {
                question: "Is the portfolio audit really free?",
                answer: "Yes! Upload your statements and get a FREE detailed analysis including performance review, risk assessment, and improvement recommendations."
              },
              {
                question: "What is your track record?",
                answer: "Serving clients since 1996 (25+ years). Managing ₹500+ crores for 5000+ satisfied clients with a systematic, diversified approach."
              }
            ].map((faq, index) => {
              const isExpanded = expandedFaq === index;
              return (
                <div key={index} className="bg-white rounded-2xl shadow-lg overflow-hidden">
                  <button
                    onClick={() => setExpandedFaq(isExpanded ? null : index)}
                    className="w-full px-6 py-5 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                  >
                    <span className="text-lg font-semibold text-gray-900 pr-8">{faq.question}</span>
                    {isExpanded ? (
                      <Minus className="h-5 w-5 text-blue-600 flex-shrink-0" />
                    ) : (
                      <Plus className="h-5 w-5 text-blue-600 flex-shrink-0" />
                    )}
                  </button>
                  {isExpanded && (
                    <div className="px-6 pb-5">
                      <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          
          <div className="mt-12 text-center">
            <Link
              to="/contact"
              className="inline-flex items-center bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-4 rounded-xl font-bold text-lg hover:from-blue-700 hover:to-purple-700 transition-all transform hover:scale-105 shadow-xl"
            >
              Contact Us
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>
      
      {/* Risk Disclaimer */}
      <section className="py-8 bg-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-6 rounded">
            <div className="flex">
              <div className="ml-3">
                <p className="text-sm text-yellow-800">
                  <strong>Investment Disclaimer:</strong> All investments are subject to market risks. 
                  Past performance does not guarantee future results. The expected returns mentioned 
                  are indicative and may vary based on market conditions. Please read all scheme-related 
                  documents carefully before investing. Consult with our advisors for personalized 
                  investment guidance.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Modals */}
      <BookingModal 
        isOpen={isBookingModalOpen} 
        onClose={() => setIsBookingModalOpen(false)} 
      />
      <CalculatorModal 
        isOpen={isCalculatorModalOpen} 
        onClose={() => setIsCalculatorModalOpen(false)} 
      />
    </div>
  );
};

export default Services;